import format from 'date-fns/format'
import parse from 'date-fns/parse'
import getConfig from 'next/config'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import {
  DEFAULT_IMAGE_URL,
  FUSION_DATE_FORMAT,
  IMAGE_DOMAIN_BASE_URL,
  IMAGE_WIDTH_AWAZE,
  IMAGE_WIDTH_NOVASOL,
  NUM_OF_USP_TO_SHOW,
  OPTIMIZELY_EVENTS,
  OPTIMIZELY_FLAGS_ENUM,
  PETS_STAY_FREE_ID,
  PRICE_FROM_NIGHTS,
  PROPERTY_CALLOUT_BADGE_ID,
  RESULTS_PAGE_SIZE,
} from 'src/constants'
import { useOptimizelyDecisionsContext } from 'src/context/optimizely-decisions-context'

import { Box } from '@awaze/design-system.components.box'
import {
  ListViewBullets as ListViewIcon,
  Map as MapIcon,
} from '@awaze/design-system.components.icon'
import { Pagination } from '@awaze/design-system.components.pagination'
import { ResultsCount } from '@awaze/design-system.components.results-count'
import { SortOptions } from '@awaze/design-system.components.sort-options'
import { Stack } from '@awaze/design-system.components.stack'

import {
  SegmentedControl,
  SegmentedControls,
} from '@awaze/design-system.components.segmented-control'
import {
  Image,
  Property,
} from '@awaze/design-system.compositions.property-list/dist/property-list.types'
import { SearchControls } from '@awaze/design-system.compositions.search-controls'
import { useWindowWidth } from '@awaze/design-system.utils.react'
import { getBrandConfig } from '@awaze/mfe-common.configs.brand'
import {
  PAGINATION_CLICK,
  SORT_ORDER_UPDATED,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awaze/mfe-common.contexts.search'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
} from '@awaze/mfe-common.contexts.tms'
import type { CommonSite } from '@awaze/mfe-common.types-and-mocks.site-search-service'

import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import { DynamicMap } from 'src/components/DynamicMap/DynamicMap'
import {
  getIsDesktop,
  getIsTablet,
  onDesktop,
} from 'src/components/utils/dimension'
import { ApplicationConfigContext } from 'src/lib/applicationConfig'
import { useDisplayContext } from 'src/lib/display/displayContext'
import { getPropertyInfo } from 'src/utils/getPropertyInfo'
import { scrollTo } from 'src/utils/helpers'
import {
  getSortOptionsFromConfig,
  shouldDisplaySortOptions,
} from 'src/utils/sortOptions'

import { Button } from '@awaze/design-system.components.button'
import {
  PropertyList as PropertyListBase,
  PropertyListProps,
} from '@awaze/design-system.compositions.property-list'
import { PropertyList as PropertyListGD1 } from '@awaze/design-system.compositions.property-list.gd-1'
import {
  PropertyList as PropertyListGD3,
  PropertyListProps as PropertyListPropsGD3,
} from '@awaze/design-system.compositions.property-list.gd-3'

import {
  SearchFormConfig,
  SearchFormFields,
} from '@awaze/design-system.compositions.search-form'
import { PERFORMANCE_MARKS } from 'src/constants/performance-marks'
import { SearchResultsTranslations } from 'src/types'
import { dataLayerErrorHandler } from 'src/utils/dataLayerErrorHandler'
import { setDisplayModeWrapper } from 'src/utils/displayModeOnClickHelper'
import { getImagePerformanceExperimentVariables } from 'src/utils/optimizely/experiments/age-1824'
import { modifyPropertyForUndatedVariants } from 'src/utils/optimizely/experiments/gd-3-was-now'
import { trackClientSideOptimizelyEvent } from 'src/utils/optimizely/trackOptimizelyEvent'

import { decode } from 'html-entities'
import { ConditionalMapBlock } from 'src/components/ConditionalMapBlock/ConditionalMapBlock'
import { Assert } from 'src/utils/assert'
import {
  carouselInteractFactory,
  formatUrlsForImageArray,
  hasFeature,
  removeFloorplanImagesFromImageArray,
} from './helpers'

const ControlsContainer = styled(Stack)`
  padding-bottom: 0;
  ${onDesktop} {
    padding-bottom: 16px;
  }

  > div {
    padding-right: 0;
  }
`

export interface GetMarkersArgs {
  ne?: string
  sw?: string
}

export const SearchResults: React.FC<{
  openFilters?: () => void
  defaultConfig: SearchFormConfig
  initialValues: SearchFormFields
  translations: SearchResultsTranslations
  isFilterDrawerOpen: boolean
}> = ({
  openFilters,
  defaultConfig,
  initialValues,
  translations,
  isFilterDrawerOpen,
}) => {
  const dispatch = useSearchContextDispatch()
  const { setDisplayMode, displayMode } = useDisplayContext()
  const { formatCurrencyToObject, formatDate, formatMessage, locale } =
    useIntlContext()

  const imagePerformanceExperimentVariables =
    getImagePerformanceExperimentVariables()

  const {
    aggregations,
    hasResults,
    loading,
    properties = [],
    rhsLevel,
    searchCriteria,
    total = 0,
    totalPages = 1,
    totalSelectedFilters,
  } = useSearchContextState()

  const searchData = {
    aggregations,
    properties,
    rhsLevel,
    total,
  }

  Assert.defined(searchCriteria, 'searchCriteria should not be undefined!')

  const { accommodationType, nights, page, salesMarket, sort, start } =
    searchCriteria

  const { brand, themeBrand } = useContext(ApplicationConfigContext)
  const tms = useContext(TmsContext)
  const setDisplayModeWithTms = setDisplayModeWrapper(tms, setDisplayMode)
  const {
    deposits,
    pdpUrlSegment,
    mapOptions: { maxZoomLevel },
  } = getBrandConfig(brand)
  const width = useWindowWidth()
  const isDesktop = getIsDesktop(width)
  const isTablet = getIsTablet(width)
  const isDatedSearch = Boolean(start)
  const displayPagination = totalPages >= 1

  const optiContext = useOptimizelyDecisionsContext()

  const displayFormattedImages = optiContext?.isExperimentVariationKeyOneOf(
    OPTIMIZELY_FLAGS_ENUM.AGE_2854_PROPERTY_CARD_IMAGE_SIZING,
    ['variant_a'],
  )

  const wasNowUndatedSearchExperimentKey =
    optiContext?.getExperimentVariationKey(
      OPTIMIZELY_FLAGS_ENUM.GD_3_WAS_NOW_UNDATED,
    )

  type CommonPropertyListProps = PropertyListProps | PropertyListPropsGD3
  let PropertyList: React.FC<CommonPropertyListProps> = PropertyListBase

  if (displayFormattedImages) {
    PropertyList = PropertyListGD1
  }

  if (wasNowUndatedSearchExperimentKey !== 'off') {
    PropertyList = PropertyListGD3
  }

  const params = new URLSearchParams(Object.entries(searchCriteria))
  params.set('newflex', 'true')
  const queryParams = params.toString()

  const shouldShowMapButton = displayMode === 'LIST'
  const shouldShowListButton = displayMode === 'MAP'

  // We only want to render the map client side, otherwise the Google Map loader
  const [shouldRenderMap, setShouldRenderMap] = useState(false)

  /** AGE 2164 experiment to switch which version of property list we use */
  const recommendedSortExperimentActiveHos =
    optiContext?.isExperimentVariationKeyOneOf(
      OPTIMIZELY_FLAGS_ENUM.AGE_SEARCH_RECOMMENDED_SORT_HOS,
      ['variant_a'],
    )

  // GD-131: OneSearch V1 VS V2
  const gd131VariationKey = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.GD_128_ONESEARCH_V2,
  )
  const isInGd131 = gd131VariationKey === 'variant_a'

  // TODO: Make the property list more resilient and not reliant on property field
  const isSite =
    accommodationType === 'lodges' || accommodationType === 'holiday-parks'

  const { publicRuntimeConfig } = getConfig()
  const { APP_ENV } = publicRuntimeConfig
  const googleApiKey = publicRuntimeConfig[APP_ENV]?.googleMapsApiKey ?? ''

  const displaySortOptions = shouldDisplaySortOptions({
    displayMode,
  })

  let sortOptionsFromConfig = getSortOptionsFromConfig({
    accommodationType,
    brand,
    startDate: start,
  })

  if (gd131VariationKey === 'variant_a') {
    if (isDatedSearch) {
      sortOptionsFromConfig = [
        'recommended',
        'priceasc',
        'pricedsc',
        'sleepsasc',
        'sleepsdsc',
        'reviewsdsc',
        'nosort',
      ]
    } else {
      sortOptionsFromConfig = [
        'recommended',
        'nosort',
        'sleepsasc',
        'sleepsdsc',
        'reviewsdsc',
      ]
    }
  }

  const sortOptions = sortOptionsFromConfig.map((option) => ({
    text: formatMessage({
      id: `search-controls.sort.option-names.${option}`,
    }),
    value: option,
  }))

  const hasRecommendedSortOptionInConfig = sortOptions.some(
    (option) => option.value === 'recommended',
  )

  if (
    recommendedSortExperimentActiveHos ||
    (sort === 'recommended' && !hasRecommendedSortOptionInConfig)
  ) {
    sortOptions.unshift({ text: 'Recommended', value: 'recommended' })
  }

  const sortOptionLabels = {
    selectOption: formatMessage({
      id: 'search-controls.sort.select-option',
      defaultMessage: 'Select sort option',
    }),
    selectedOptionPrefix: formatMessage({
      id: 'search-controls.sort.selected-option-prefix',
      defaultMessage: 'Sort',
    }),
  }

  const pdpUrlSegmentTranslationKey =
    pdpUrlSegment[accommodationType] || pdpUrlSegment['default']

  useEffect(() => {
    setShouldRenderMap(true)
  }, [])

  const propertyImageWidth =
    brand === 'novasol' || brand === 'fincallorca'
      ? IMAGE_WIDTH_NOVASOL
      : IMAGE_WIDTH_AWAZE

  const urgencyNudgeMessagingEnabled = brand === 'cottages.com'
  const urgencyNudgeMessagingText = "Book now before it's gone!" // BLAKE TO CONFIRM TEXT

  interface CommonSiteWithExtraProps extends CommonSite {
    urgencyNudgeMessaging?: boolean
  }

  const getPropertyListMappedProperty = (
    property: CommonSiteWithExtraProps,
  ): Property => {
    const imagesWithoutFloorplans = property.images
      ? removeFloorplanImagesFromImageArray(property.images)
      : []

    let finalImageArray: Image[]

    if (imagesWithoutFloorplans.length === 0) {
      finalImageArray = [
        {
          imageURL: DEFAULT_IMAGE_URL,
          imgAlt: `${decode(property.displayName)} images coming soon`,
        },
      ]
    } else {
      finalImageArray = formatUrlsForImageArray(
        imagesWithoutFloorplans,
        propertyImageWidth,
        brand,
      )
    }

    Assert.defined(property.features, 'response contained no features')

    const remappedProperty = {
      ...property,
      property: {
        ...property.property,
      },
      images: finalImageArray,
      features: property.features,
      priceFrom: property.priceFrom,
      displayName: property.displayName || '',
      rhs3: property.rhs3,
      grade: property.grade ?? '',
      location: property.location,
      averageReviewScore: property.averageReviewScore,
      hasCallout: property.isCustomerChoice
        ? property.isCustomerChoice
        : hasFeature({
            features: property.features,
            featureToCheck: PROPERTY_CALLOUT_BADGE_ID,
          }),
      hasPetsFree:
        property.petsGoFree ??
        hasFeature({
          features: property.features,
          featureToCheck: PETS_STAY_FREE_ID,
        }),
      startDate:
        isSite && start
          ? format(parse(start, FUSION_DATE_FORMAT, new Date()), 'yyyy-MM-dd')
          : property.startDate,
      price: property.price,
      wasPrice: property.wasPrice,
      USPs: property.USPs ?? [],
      ...(property.lengthOfStay && { lengthOfStay: property.lengthOfStay }),
      urgencyNudgeMessaging:
        urgencyNudgeMessagingEnabled && property.urgencyNudgeMessaging,
    }

    return modifyPropertyForUndatedVariants<Property>(
      remappedProperty,
      wasNowUndatedSearchExperimentKey,
      isDatedSearch,
      brand,
    )
  }

  const formattedPropertiesResponse = properties.map((property) =>
    getPropertyListMappedProperty(property),
  )

  return (
    <Stack fullWidth align="center">
      <ControlsContainer
        isHorizontal
        fullWidth
        justify="space-between"
        align="baseline"
      >
        <span
          style={{
            opacity: !loading && !hasResults && displayMode === 'LIST' ? 0 : 1,
            paddingTop: 8,
            paddingBottom: 12,
          }}
        >
          <ResultsCount
            count={
              !hasResults && !loading ? 0 : total || ('—' as unknown as number)
            }
            labels={{
              textSingle: formatMessage({
                id: 'search-layout.property-count-single',
                defaultMessage: 'property',
              }),
              textMultiple: formatMessage({
                id: 'search-layout.property-count-multiple',
                defaultMessage: 'properties',
              }),
            }}
          />
        </span>

        {(hasResults || displayMode === 'MAP') && (
          <>
            {isDesktop && (
              <SegmentedControls>
                {displaySortOptions && (
                  <SortOptions
                    disablePadding
                    sortOptions={sortOptions}
                    selectedOption={sort}
                    onOptionChange={(sort: string) =>
                      dispatch({ type: SORT_ORDER_UPDATED, sort })
                    }
                    labels={sortOptionLabels}
                  />
                )}
                {shouldShowMapButton && (
                  <Button
                    text={formatMessage({
                      id: 'search-controls.map.title',
                      defaultMessage: 'Map',
                    })}
                    icon={<MapIcon />}
                    onClick={() => {
                      window?.performance &&
                        window.performance.mark(
                          PERFORMANCE_MARKS.MAP_VIEW_BUTTON_CLICKED,
                        )
                      setDisplayModeWithTms('MAP')
                      trackClientSideOptimizelyEvent(
                        OPTIMIZELY_EVENTS.MAP_CLICK,
                        optiContext,
                      )
                    }}
                    colourScheme="brand-secondary"
                    size="small"
                  />
                )}
                {shouldShowListButton && (
                  <SegmentedControl
                    text={formatMessage({
                      id: 'search-controls.list.title',
                      defaultMessage: 'Back to list view',
                    })}
                    ariaLabel={formatMessage({
                      id: 'search-controls.list.aria',
                      defaultMessage: 'Back to list view',
                    })}
                    icon={<ListViewIcon />}
                    variant="medium"
                    onClick={() => {
                      setDisplayModeWithTms('LIST')
                      dispatch({ type: PAGINATION_CLICK, page: 1 })
                    }}
                  />
                )}
              </SegmentedControls>
            )}
            {isTablet && (
              <SearchControls
                filtersCount={totalSelectedFilters?.toString() || '0'}
                sorting={
                  displaySortOptions
                    ? {
                        sortOptions: sortOptions,
                        selectedOption: sort,
                        onOptionChange: (sort: string) =>
                          dispatch({ type: SORT_ORDER_UPDATED, sort }),
                        labels: sortOptionLabels,
                      }
                    : undefined
                }
                showFilters={openFilters}
                justify="end"
                displayMode={displayMode}
                setDisplayMode={setDisplayModeWithTms}
                translations={{
                  filterTitle: formatMessage({
                    id: 'search-controls.filter.title',
                    defaultMessage: 'Filters',
                  }),
                  filterAriaText: formatMessage({
                    id: 'search-controls.filter.aria',
                    defaultMessage: 'Open filters',
                  }),
                  listTitle: formatMessage({
                    id: 'search-controls.list.title',
                    defaultMessage: 'Back to list view',
                  }),
                  listAriaText: formatMessage({
                    id: 'search-controls.list.aria',
                    defaultMessage: 'Back to list view',
                  }),
                  mapTitle: formatMessage({
                    id: 'search-controls.map.title',
                    defaultMessage: 'Map',
                  }),
                  mapAriaText: formatMessage({
                    id: 'search-controls.map.aria',
                    defaultMessage: 'Map View',
                  }),
                }}
              />
            )}
          </>
        )}
      </ControlsContainer>

      {shouldRenderMap && displayMode === 'MAP' && (
        <ConditionalMapBlock
          defaultConfig={defaultConfig}
          initialValues={initialValues}
          isDesktop={isDesktop}
          locale={locale}
          marketCode={searchCriteria.salesMarket || '826'}
          openFilters={openFilters}
          translations={translations}
          enableRemoveScroll={!isFilterDrawerOpen}
        >
          <DynamicMap
            maxZoom={maxZoomLevel}
            apiKey={googleApiKey}
            brand={brand}
            themeBrand={themeBrand}
            fullHeight={!isDesktop}
            getPropertyInfo={(propertyId) =>
              getPropertyInfo({
                brand,
                oneSearchVersion: isInGd131 ? 'v2' : 'v1',
                propertyId,
                salesMarket,
                searchCriteria,
              })
            }
            translations={{
              failedMapPlaceholderHeading: formatMessage({
                id: 'location-section.map',
                defaultMessage: 'Map',
              }),
              failedMapPlaceholderText: formatMessage({
                id: 'location-section.map-failed',
                defaultMessage: 'Failed to load map. Please try and reload.',
              }),
              searchFormMapAreaText: formatMessage({
                id: 'search-results.map.searchFormMapAreaText',
                defaultMessage: 'Map area',
              }),
            }}
            pdpUrlSegment={formatMessage({
              id: pdpUrlSegmentTranslationKey,
            })}
          />
        </ConditionalMapBlock>
      )}

      {displayMode === 'LIST' && (
        <PropertyList
          loading={loading}
          results={formattedPropertiesResponse}
          nights={isDatedSearch ? nights : PRICE_FROM_NIGHTS}
          config={{
            translations: {
              priceUnavailableCTALabel: formatMessage({
                id: 'search-results.property-list.unavailable-price',
                defaultMessage: 'For price, add a start date',
              }),
              calloutBadge: formatMessage({
                id: 'search-results.property-list.callout-badge',
                defaultMessage: "Customers' Choice",
              }),
              reviewRating: formatMessage({
                id: 'search-results.property-list.rating',
                defaultMessage: 'Feefo rating',
              }),
              outOf: formatMessage({
                id: 'search-results.property-list.out-of',
                defaultMessage: 'out of',
              }),
              stars: formatMessage({
                id: 'search-results.property-list.stars',
                defaultMessage: 'stars',
              }),
              nights: formatMessage({
                id: 'search-results.property-list.nights',
                defaultMessage: 'nights',
              }),
              from: formatMessage({
                id: 'search-results.property-list.from',
                defaultMessage: 'From',
              }),
              linkTo: formatMessage({
                id: 'search-results.property-list.link-to',
                defaultMessage: 'Link to',
              }),
              propertyLinkLabel: formatMessage({
                id: 'search-results.property-list.view',
                defaultMessage: 'View',
              }),
              urgencyBadge: formatMessage({
                id: 'search-results.property-list.urgencyMessage',
                defaultMessage: urgencyNudgeMessagingText,
              }),
              dateFormatter: (date: string) => {
                const UTCDate = new Date(`${date}T00:00:00`)

                return `${formatDate(UTCDate, { day: 'numeric' })} ${formatDate(
                  UTCDate,
                  {
                    month: 'short',
                  },
                )}`
              },
              reserveTodayForOnly: formatMessage({
                id: 'search-results.property-list.reserve-today',
                defaultMessage: 'Reserve today for only',
              }),
              reserveForSelectedProperties: 'on selected properties',
              includedExtrasLabel: formatMessage({
                id: 'search-results.property-list.includedExtras',
                defaultMessage: 'Price includes',
              }),
              propertyLinkAriaLabel: (title) =>
                formatMessage(
                  {
                    id: 'search-results.property-list.link-aria-label',
                    defaultMessage: 'view {title} (opens in a new window)',
                  },
                  { title },
                ),
              formatCurrency: formatCurrencyToObject,
            },
            USPCount: NUM_OF_USP_TO_SHOW,
            baseImg: IMAGE_DOMAIN_BASE_URL,
            defaultImg: DEFAULT_IMAGE_URL,
            resultsPageSize: RESULTS_PAGE_SIZE,
            dateToday: new Date(),
            searchParameters: queryParams,
            accommodationType:
              accommodationType === 'cottages' ? 'cottages' : 'accom',
            pdpUrlSegment: formatMessage({
              id: pdpUrlSegmentTranslationKey,
            }),
            imageWidth: propertyImageWidth,
            numberImagesFetchPriorityHigh:
              imagePerformanceExperimentVariables.config
                .numberImagesFetchPriorityHigh,
            numberImagesPriority:
              imagePerformanceExperimentVariables.config.numberImagesPriority,
            numberImagesToEagerLoad:
              imagePerformanceExperimentVariables.config
                .numberImagesToEagerLoad,
          }}
          onPropertyLinkClick={({ propertyCode }) => {
            new DataLayerFactory()
              .getEvent(DataLayerEventType.PropertyClick)
              .update(
                tms,
                {
                  propertyCode,
                  searchCriteria,
                  searchData,
                },
                dataLayerErrorHandler,
              )
            trackClientSideOptimizelyEvent(
              OPTIMIZELY_EVENTS.PDP_CLICK_THROUGH,
              optiContext,
            )
          }}
          deposits={deposits}
          onCarouselInteractFactory={(
            imageArray: Array<Image>,
            displayName: string,
          ) => carouselInteractFactory(imageArray, displayName, tms)}
        />
      )}

      {displayPagination && displayMode === 'LIST' && (
        <Box padding={[16, 16, 16]}>
          <Pagination
            ariaTranslations={{
              ariaLabel: formatMessage({
                id: 'search-results.pagination.aria-page',
                defaultMessage: 'Page',
              }),
              ariaLabelActive: formatMessage({
                id: 'search-results.pagination.aria-current-page',
                defaultMessage: 'Current page, Page',
              }),
              ariaNextPage: formatMessage({
                id: 'search-results.pagination.next',
                defaultMessage: 'next',
              }),
              ariaPreviousPage: formatMessage({
                id: 'search-results.pagination.previous',
                defaultMessage: 'previous',
              }),
            }}
            activePage={page}
            totalPages={totalPages}
            onPaginationClick={(page) => {
              scrollTo(document.body)
              setTimeout(() => {
                dispatch({ type: PAGINATION_CLICK, page })
              }, 100)
            }}
          />
        </Box>
      )}
    </Stack>
  )
}
